import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { XIcon } from "lucide-react";

type DialogSize = "sm" | "md" | "lg";

const DialogContext = React.createContext<{ size: DialogSize } | null>(null);
const useDialogContext = () => {
  const context = React.useContext(DialogContext);
  if (!context) {
    throw new Error("DialogContext not found");
  }
  return context;
};

function Dialog({ size = "md", ...props }: DialogPrimitive.Root.Props & { size?: DialogSize }) {
  return (
    <DialogContext.Provider value={{ size }}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </DialogContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/80 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50",
        className,
      )}
      {...props}
    />
  );
}

const DialogContentSizes = {
  lg: {
    className:
      "[--dialog-top-gap:8vh] sm:[--dialog-top-gap:10vh] [--dialog-bottom-gap:1.5rem] sm:[--dialog-bottom-gap:2rem]",
  },
  md: {
    className:
      "[--dialog-top-gap:16vh] sm:[--dialog-top-gap:20vh] [--dialog-bottom-gap:1.5rem] sm:[--dialog-bottom-gap:2rem]",
  },
  sm: {
    className:
      "[--dialog-top-gap:24vh] sm:[--dialog-top-gap:30vh] [--dialog-bottom-gap:1.5rem] sm:[--dialog-bottom-gap:2rem]",
  },
};

function DialogContent({
  className,
  containerClassName,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  containerClassName?: string;
  showCloseButton?: boolean;
}) {
  const { size } = useDialogContext();
  return (
    <DialogPortal>
      <DialogOverlay />
      <div
        className={cn(
          "fixed inset-0 z-50 overflow-y-auto px-4 pt-[var(--dialog-top-gap)] pb-[var(--dialog-bottom-gap)] sm:px-6",
          DialogContentSizes[size].className,
          containerClassName,
        )}
      >
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className={cn(
            "data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 relative mx-auto grid w-full max-w-[calc(100%-2rem)] max-h-[calc(100dvh-var(--dialog-top-gap)-var(--dialog-bottom-gap))] origin-top gap-4 overflow-y-auto overscroll-contain rounded-xl bg-background p-4 text-xs/relaxed outline-none ring-1 ring-foreground/10 duration-100 data-closed:animate-out data-open:animate-in sm:max-w-lg",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={<Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" />}
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("gap-1 flex flex-col", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("gap-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-sm font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-xs/relaxed *:[a]:underline *:[a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
